import React, { useMemo } from "react";

// Classifies a dataGroup question as numeric/categorical using the backend's
// per-SPSS-variable classification (variableTypes), falling back to the
// question's own `type` field when the backend hasn't returned yet.
function classifyQuestion(qKey, dataGroup, variableTypes) {
  const q = dataGroup[qKey];
  const names = (q?.varName || []).map((v) => v.Name).filter(Boolean);
  if (!names.length) return { role: "categorical", names, single: false };

  if (variableTypes) {
    const allNumeric = names.every((n) => variableTypes.numeric?.includes(n));
    return { role: allNumeric ? "numeric" : "categorical", names, single: names.length === 1 };
  }

  const t = (q?.type || "").toLowerCase();
  const isNumeric = t === "numeric" || t.includes("numeric");
  return { role: isNumeric ? "numeric" : "categorical", names, single: names.length === 1 };
}

/**
 * A question-level variable picker for the analysis params form.
 * `value` / `onChange` operate on dataGroup question KEYS (not raw SPSS
 * variable names) so the parent can resolve to flattened var-name lists
 * only when submitting the analysis.
 */
function VariablePicker({ dataGroup, variableTypes, role = "any", multiple, value, onChange, requireSingleVar = false }) {
  const options = useMemo(() => {
    return Object.keys(dataGroup || {})
      .map((key) => ({ key, ...classifyQuestion(key, dataGroup, variableTypes) }))
      .filter((o) => o.names.length > 0)
      .filter((o) => (role === "any" ? true : o.role === role))
      .filter((o) => (requireSingleVar ? o.single : true));
  }, [dataGroup, variableTypes, role, requireSingleVar]);

  if (multiple) {
    const selected = Array.isArray(value) ? value : [];
    return (
      <select
        multiple
        size={Math.min(8, Math.max(4, options.length))}
        className="analysis-select"
        value={selected}
        onChange={(e) => onChange(Array.from(e.target.selectedOptions, (o) => o.value))}
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {dataGroup[o.key]?.Qtitle ? `${o.key} — ${dataGroup[o.key].Qtitle}` : o.key}
          </option>
        ))}
      </select>
    );
  }

  return (
    <select className="analysis-select" value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select a variable...</option>
      {options.map((o) => (
        <option key={o.key} value={o.key}>
          {dataGroup[o.key]?.Qtitle ? `${o.key} — ${dataGroup[o.key].Qtitle}` : o.key}
        </option>
      ))}
    </select>
  );
}

export function resolveKeysToVarNames(keys, dataGroup) {
  return (Array.isArray(keys) ? keys : [keys])
    .filter(Boolean)
    .flatMap((key) => (dataGroup[key]?.varName || []).map((v) => v.Name))
    .filter(Boolean);
}

export default VariablePicker;
